/**
 * Portal Page cover geometry — design_handoff_portal_profile_editor (Oct 2 2026), "Cover layout
 * geometry". Ported as-is from the prototypes' `stackTiles()` / `geom()` so the public page, the
 * editor's mini previews and the live preview place every photo identically.
 *
 * A tile is `{ i, x, y, w, h, more }`: the photo index and its box in PERCENT of the cover; `more`
 * is the "+N" overflow count on the last stacked thumb when more than four photos exist.
 */

export const DESKTOP_LAYOUTS = {
  mosaic: ['MOSAIC', 'Photo 1 leads, the rest stack beside it.'],
  poster: ['POSTER', 'Photo 1 stands whole on the right.'],
  backdrop: ['FULL BLEED', 'Photo 1 fills the cover. Text sits on a black fade.'],
  row: ['ROW', 'Up to four photos side by side.'],
};

export const MOBILE_LAYOUTS = {
  mosaic: ['MOSAIC', 'Photo 1 leads, three stacked beside it.'],
  carousel: ['SWIPE', 'One photo at a time with arrows and dots.'],
  backdrop: ['FULL BLEED', 'A tall photo with your name over it.'],
};

/** Mobile cover height = content width × ratio (capped at 720px by the page). */
export const MOBILE_RATIO = { mosaic: 0.95, carousel: 1.25, backdrop: 1.35 };

export const DEFAULT_LAYOUT_DESKTOP = 'mosaic';
export const DEFAULT_LAYOUT_MOBILE = 'mosaic';

export const desktopLayout = (k) => (Object.prototype.hasOwnProperty.call(DESKTOP_LAYOUTS, k) ? k : DEFAULT_LAYOUT_DESKTOP);
export const mobileLayout = (k) => (Object.prototype.hasOwnProperty.call(MOBILE_LAYOUTS, k) ? k : DEFAULT_LAYOUT_MOBILE);

/** The lead photo at x0 (width lw) plus up to three thumbs stacked in the remaining width. */
export function stackTiles(n, x0, lw) {
  const T = (i, x, y, w, h, more) => ({ i, x, y, w, h, more });
  if (n === 1) return [T(0, x0, 0, 100 - x0, 100, 0)];
  const ex = n - 1;
  const k = Math.min(ex, 3);
  const more = ex > 3 ? n - 3 : 0;
  const xr = x0 + lw;
  const wr = 100 - xr;
  const tiles = [T(0, x0, 0, lw, 100, 0)];
  for (let j = 0; j < k; j++) tiles.push(T(j + 1, xr, (100 / k) * j, wr, 100 / k, more && j === k - 1 ? more : 0));
  return tiles;
}

/**
 * Tiles + the text column width for a layout. `cur` is the swipe index on the mobile carousel.
 * `fade` marks the desktop full-bleed, which draws the left-to-right black fade under the text.
 */
export function geom(layout, n, mobile, cur = 0) {
  const T = (i, x, y, w, h) => ({ i, x, y, w, h, more: 0 });
  if (!n) return { tiles: [], textW: 100 };
  if (mobile) {
    if (layout === 'carousel') return { tiles: [T(((cur % n) + n) % n, 0, 0, 100, 100)], textW: 100 };
    if (layout === 'backdrop') return { tiles: [T(0, 0, 0, 100, 100)], textW: 100 };
    return { tiles: stackTiles(n, 0, 64), textW: 100 };
  }
  if (layout === 'backdrop') return { tiles: [T(0, 0, 0, 100, 100)], textW: 54, fade: true };
  if (layout === 'poster' || n === 1) return { tiles: [T(0, 62, 0, 38, 100)], textW: 62 };
  if (layout === 'row') {
    const k = Math.min(n, 4);
    const ww = 60 / k;
    return { tiles: Array.from({ length: k }, (_, i) => T(i, 40 + ww * i, 0, ww, 100)), textW: 40 };
  }
  return { tiles: stackTiles(n, 40, 36), textW: 40 };
}

/** Inner padding that draws the gutter: tiles off the left / top edge give up `gap` px on that side. */
export const tilePad = (t, gap) => `${t.y > 0.5 ? gap : 0}px 0 0 ${t.x > 0.5 ? gap : 0}px`;

export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/**
 * Pointer-drag focal maths (the editor's `drag()`): how far the image overflows a box on each
 * axis, from its natural aspect `ratio` (w / h) and the box size. A drag of Δpx moves the focal
 * point by Δ / overflow × 100 — the image follows the finger exactly.
 */
export function overflowFor(ratio, bw, bh) {
  const r = ratio || 1;
  const landscapeInBox = r > bw / bh;
  return { ox: landscapeInBox ? bh * r - bw : 0, oy: landscapeInBox ? 0 : bw / r - bh };
}
